// Thin aliases over Excalidraw's real types plus the scene (de)serialization
// helpers.  We alias rather than redeclare so values flow to/from the
// <Excalidraw> component props without casts:
//   ExcalElement   = ExcalidrawElement       (the element union)
//   ExcalAppState  = Partial<AppState>        (we only ever read/persist a few)
//   ExcalidrawApi  = ExcalidrawImperativeAPI  (the imperative ref API)

import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

export type ExcalElement = ExcalidrawElement;
export type ExcalAppState = Partial<AppState>;
export type ExcalidrawApi = ExcalidrawImperativeAPI;
export type { BinaryFiles };

// Loose collaborator shape for our cursor map — a structural subset of
// Excalidraw's Collaborator built from network messages.  Cast to the real
// type at the single updateScene({collaborators}) call site.
export type Collaborator = {
  pointer?: { x: number; y: number };
  button?: "up" | "down";
  selectedElementIds?: Record<string, boolean>;
  username?: string | null;
  color?: { background: string; stroke: string };
};

/** Parse the stored scene blob into Excalidraw initial data. */
export function parseScene(raw: string): {
  elements: ExcalElement[];
  appState: ExcalAppState;
} {
  try {
    const val = JSON.parse(raw) as {
      elements?: unknown;
      appState?: unknown;
    } | null;
    if (val && typeof val === "object") {
      return {
        elements: Array.isArray(val.elements)
          ? (val.elements as ExcalElement[])
          : [],
        appState: (val.appState as ExcalAppState) ?? {},
      };
    }
  } catch {
    /* fall through to empty */
  }
  return { elements: [], appState: {} };
}

/** Persist only a whitelist of appState — the rest (selection, scroll, zoom,
 *  collaborators, viewport size) is per-device/ephemeral and would both bloat
 *  the blob and clobber other devices' viewports. */
export function pickAppState(appState: ExcalAppState): ExcalAppState {
  return { viewBackgroundColor: appState.viewBackgroundColor ?? "#ffffff" };
}

// Keep deletions in the blob as `isDeleted` tombstones, not as an absence:
// the durable rebase now unions the row into the live canvas (see
// reconcile.ts), so a deleted element must travel as a higher-version
// tombstone or a peer would resurrect its older live copy.  Tombstones are
// GC'd after this window — long enough to converge online collaborators,
// bounded so they can't grow the blob toward the 2MB save cap forever.  A peer
// offline past the window may resurrect a delete; that narrow case is left to a
// future server-side merge.
const TOMBSTONE_TTL_MS = 24 * 60 * 60 * 1000;

/** Serialize elements + appState to the stored blob shape.  Live elements plus
 *  recent deletion tombstones are kept; stale tombstones are dropped. */
export function serializeScene(
  elements: readonly ExcalElement[],
  appState: ExcalAppState,
  now: number = Date.now(),
): string {
  const cutoff = now - TOMBSTONE_TTL_MS;
  const kept = elements.filter(
    (e) =>
      !e.isDeleted || (typeof e.updated === "number" && e.updated >= cutoff),
  );
  return JSON.stringify({ v: 1, elements: kept, appState: pickAppState(appState) });
}

/** Collect the image fileIds referenced by a set of elements. */
export function collectFileIds(elements: readonly ExcalElement[]): string[] {
  const ids = new Set<string>();
  for (const el of elements) {
    if (el.isDeleted) continue;
    if ("fileId" in el && typeof el.fileId === "string" && el.fileId) {
      ids.add(el.fileId);
    }
  }
  return [...ids];
}
